import React, { useState } from "react";
import { Box, Typography, IconButton, Menu, MenuItem } from "@mui/material";
import { Droppable } from "react-beautiful-dnd";
import PlayerItemMinimal from "./PlayerItemMinimal";
import { PRO_COLORS_MAP } from "../../pages/proColors";

interface ProColorMapping {
  PROS: Record<number, [string, string]>;
  PLAYERS: Record<number, number>;
  MAPPING: Record<number, string>;
}

interface ChukkerListDenseProps {
  managerMode: number;
  chukkerList: Record<string, Record<string, Record<string, any[]>>>;
  referenceTeam?: (id_sender: string, chukker_sender: string, id_receiver: string) => void;
  referencePlayer?: (
    id_sender: string,
    chukker_sender: string,
    team_sender: string,
    id_receiver: string,
    chukker_receiver: string
  ) => void;
  useUSPARating: boolean;
  proColorMapping?: ProColorMapping;
  betaMode: boolean;
  chukkerDetails?: Record<string, any>;
  setChukkerDetails?: (details: Record<string, any>) => void;
  gameDivisions?: any;
  devPort?: string;
  clubId?: number;
  eventId?: number;
  eventType?: "games" | "lessons" | "tournaments";
  updateHorseAssignment?: (playerId: string, chukker: string, horseId: string | null) => void;
  allHorseAssignments?: Record<string, any[]>; // { playerId: assignments[] }
  allPlayerHorses?: Record<string, any[]>; // { playerId: horses[] }
  allClubHorses?: any[]; // All club horses
}

function ChukkerListDense(props: ChukkerListDenseProps) {
  const [colorMenuAnchor, setColorMenuAnchor] = useState<{ chukker: string; team: string; element: HTMLElement } | null>(null);
  
  const chukkerKeys = Object.keys(props.chukkerList)
    .filter((key) => key !== "-1")
    .sort((a, b) => parseInt(a) - parseInt(b));
  const benchData = props.chukkerList["-1"];
  
  const handleColorClick = (event: React.MouseEvent<HTMLElement>, chukker: string, team: string) => {
    event.stopPropagation();
    setColorMenuAnchor({ chukker, team, element: event.currentTarget });
  };

  const handleColorClose = () => {
    setColorMenuAnchor(null);
  };

  const handleColorSelect = (color: string) => {
    if (!colorMenuAnchor || !props.setChukkerDetails) return;
    
    const { chukker, team } = colorMenuAnchor;
    // Team can be either "A"/"B" (from header) or "A_1"/"B_1" (from team cell - legacy)
    const teamId = team.includes("_") ? team.split("_")[0] : team; // Extract "A" or "B"
    
    props.setChukkerDetails((prev: any) => ({
      ...prev,
      [chukker]: {
        ...prev[chukker],
        jersey_color: {
          ...prev[chukker]?.jersey_color,
          [teamId]: color
        }
      }
    }));
    handleColorClose();
  };

  // Calculate max players per team across all chukkers
  let maxPlayersPerTeam = 0;
  chukkerKeys.forEach((chukkerKey) => {
    const teams = props.chukkerList[chukkerKey];
    Object.keys(teams).forEach((teamId) => {
      const playerCount = Object.keys(teams[teamId]).length;
      if (playerCount > maxPlayersPerTeam) {
        maxPlayersPerTeam = playerCount;
      }
    });
  });

  // Get all unique team IDs (usually A and B)
  const teamIds = new Set<string>();
  chukkerKeys.forEach((chukkerKey) => {
    Object.keys(props.chukkerList[chukkerKey]).forEach((teamId) => {
      teamIds.add(teamId.split("_")[0]);
    });
  });
  const sortedTeamIds = Array.from(teamIds).sort();

  // Calculate dynamic column widths
  // Team column takes 1 part, each chukker takes 1 part
  // So if 2 chukkers: team (1) + chukker1 (1) + chukker2 (1) = 3 parts total
  const totalColumns = 1 + chukkerKeys.length;
  const teamColumnWidth = `minmax(120px, ${100 / totalColumns}%)`;
  const chukkerColumnWidth = `minmax(100px, ${100 / totalColumns}%)`;

  // Helper function to calculate handicap for a team
  const calculateHandicap = (players: Record<string, any[]>, useUSPARating: boolean): number => {
    if (!players || Object.keys(players).length === 0) return 0;
    let handicap = 0;
    Object.keys(players).forEach((key) => {
      if (useUSPARating) {
        handicap += players[key][6] || 0;
      } else {
        handicap += players[key][7] || 0;
      }
    });
    return handicap;
  };

  // Only show bench for managers (not for players)
  const hasBench = benchData && props.managerMode === -1;
  const benchRowNumber = sortedTeamIds.length + 2; // +1 for header, +1 for 1-based indexing

  // Calculate grid rows: header (auto) + team rows (distributed) + bench (auto if exists)
  // Each team row should be at least 240px to fit 4 players comfortably
  // Each player item: ~55px (padding + content + margin) * 4 = 220px + gaps = ~240px
  // When bench exists, team rows should share remaining space after bench gets its minimum

  console.log("Bench Debug:", {
    benchData: !!benchData,
    managerMode: props.managerMode,
    betaMode: props.betaMode,
    hasBench,
    benchRowNumber,
    sortedTeamIdsLength: sortedTeamIds.length
  });

  return (
    <Box sx={{ height: "calc(100vh - 0.280px)", p: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `${teamColumnWidth} repeat(${chukkerKeys.length}, ${chukkerColumnWidth})`,
            gridTemplateRows: hasBench 
              ? `auto repeat(${sortedTeamIds.length}, minmax(240px, calc((100% - 60px - 120px) / ${sortedTeamIds.length}))) minmax(120px, auto)`
              : `auto repeat(${sortedTeamIds.length}, minmax(240px, 1fr))`,
            gap: 0,
            backgroundColor: "grey.100",
            borderRadius: 2,
            overflowY: "auto",
            boxShadow: 2,
            width: "100%",
            flex: 1,
            minHeight: 0,
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
        {/* Header Row */}
        <Box
          sx={{
            position: "sticky",
            left: 0,
            zIndex: 10,
            background: "linear-gradient(135deg, #5D8AA8 0%, #4a6d85 100%)",
            p: 1,
            borderRight: "1px solid",
            borderColor: "rgba(255,255,255,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: "white", fontSize: "0.75rem" }}>
            Chukker
          </Typography>
        </Box>
        {chukkerKeys.map((chukkerKey) => {
          // Calculate handicaps for both teams in this chukker
          const teamAId = `A_${chukkerKey}`;
          const teamBId = `B_${chukkerKey}`;
          const teamAPlayers = props.chukkerList[chukkerKey]?.[teamAId] || {};
          const teamBPlayers = props.chukkerList[chukkerKey]?.[teamBId] || {};
          const teamAHandicap = calculateHandicap(teamAPlayers, props.useUSPARating);
          const teamBHandicap = calculateHandicap(teamBPlayers, props.useUSPARating);

          return (
            <Box
              key={chukkerKey}
              data-testid={`chukker-${chukkerKey}`}
              sx={{
                background: "linear-gradient(135deg, #5D8AA8 0%, #4a6d85 100%)",
                p: 1,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                borderRight: chukkerKey !== chukkerKeys[chukkerKeys.length - 1] ? "1px solid" : "none",
                borderColor: "rgba(255,255,255,0.1)",
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: "white", fontSize: "0.75rem" }}>
                {chukkerKey}
              </Typography>
              {(props.managerMode === -1 || props.managerMode !== -1) && (
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ fontWeight: 500, color: "rgba(255,255,255,0.9)", fontSize: "0.65rem" }}
                      data-testid={`handicap-A_${chukkerKey}`}
                    >
                      {props.managerMode === -1 ? `A: ${teamAHandicap}` : "Team A"}
                    </Typography>
                    {props.chukkerDetails && props.setChukkerDetails && props.managerMode === -1 && (
                      <IconButton
                        onClick={(e) => handleColorClick(e, chukkerKey, "A")}
                        size="small"
                        sx={{ 
                          p: 0.25,
                          minWidth: "auto",
                          width: "16px",
                          height: "16px",
                          backgroundColor: "white",
                          "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" }
                        }}
                      >
                        <img
                          src={(PRO_COLORS_MAP as Record<string, string>)[props.chukkerDetails[chukkerKey]?.jersey_color?.A] || (PRO_COLORS_MAP as Record<string, string>)["blue"]}
                          alt={`${props.chukkerDetails[chukkerKey]?.jersey_color?.A || "blue"} jersey`}
                          width={12}
                          height={12}
                          style={{ display: "block" }}
                        />
                      </IconButton>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ fontWeight: 500, color: "rgba(255,255,255,0.9)", fontSize: "0.65rem" }}
                      data-testid={`handicap-B_${chukkerKey}`}
                    >
                      {props.managerMode === -1 ? `B: ${teamBHandicap}` : "Team B"}
                    </Typography>
                    {props.chukkerDetails && props.setChukkerDetails && props.managerMode === -1 && (
                      <IconButton
                        onClick={(e) => handleColorClick(e, chukkerKey, "B")}
                        size="small"
                        sx={{ 
                          p: 0.25,
                          minWidth: "auto",
                          width: "16px",
                          height: "16px",
                          backgroundColor: "white",
                          "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" }
                        }}
                      >
                        <img
                          src={(PRO_COLORS_MAP as Record<string, string>)[props.chukkerDetails[chukkerKey]?.jersey_color?.B] || (PRO_COLORS_MAP as Record<string, string>)["red"]}
                          alt={`${props.chukkerDetails[chukkerKey]?.jersey_color?.B || "red"} jersey`}
                          width={12}
                          height={12}
                          style={{ display: "block" }}
                        />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}

        {/* Team Rows */}
        {sortedTeamIds.map((teamLetter, teamIndex) => (
          <React.Fragment key={teamLetter}>
            {/* Team Label Row */}
            <Box
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 9,
                backgroundColor: teamLetter === "A" ? "rgba(245, 194, 66, 0.15)" : "rgba(162, 214, 154, 0.15)",
                p: 1,
                borderRight: "1px solid",
                borderTop: teamIndex > 0 ? "3px solid" : "1px solid", // Thicker border for Team B
                borderColor: teamIndex > 0 ? "grey.400" : "grey.200", // Darker border for Team B
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary", fontSize: "0.75rem" }}>
                Team {teamLetter}
              </Typography>
              {/*(props.managerMode !== -1 || props.betaMode) && (
                <Typography variant="caption" sx={{ fontWeight: 500, color: "text.secondary", fontSize: "0.65rem" }}>
                  Avg
                </Typography>
              )*/}
            </Box>
            {chukkerKeys.map((chukkerKey) => {
              const teamId = `${teamLetter}_${chukkerKey}`;
              const players = props.chukkerList[chukkerKey]?.[teamId] || {};
              const playerEntries = Object.entries(players);

              return (
                <Box
                  key={`${chukkerKey}-${teamLetter}`}
                  sx={{
                    backgroundColor: teamLetter === "A" ? "rgba(245, 194, 66, 0.08)" : "rgba(162, 214, 154, 0.08)",
                    p: 0.5,
                    height: "100%",
                    borderTop: "3px solid",
                    borderRight: chukkerKey !== chukkerKeys[chukkerKeys.length - 1] ? "1px solid" : "none",
                    borderColor: "grey.400",
                    transition: "background-color 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      backgroundColor: teamLetter === "A" ? "rgba(245, 194, 66, 0.12)" : "rgba(162, 214, 154, 0.12)",
                    },
                  }}
                >
                  <Droppable
                    droppableId={`${chukkerKey}____${teamId}`}
                    type="TEAM"
                  >
                    {(provided: any, snapshot: any) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          height: "100%",
                          minHeight: "240px",
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          backgroundColor: snapshot.isDraggingOver ? "primary.50" : "transparent",
                          borderRadius: 1,
                          p: snapshot.isDraggingOver ? 0.5 : 0,
                          transition: "all 0.2s ease",
                          overflowY: "auto",
                        }}
                      >
                        {playerEntries.length === 0 ? (
                          <Box
                            sx={{
                              height: "calc(25% - 6px)",
                              minHeight: "calc(25% - 6px)",
                              border: "1px dashed",
                              borderColor: "grey.300",
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "grey.50",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "text.disabled", fontSize: "0.65rem" }}
                            >
                              -
                            </Typography>
                          </Box>
                        ) : (
                          playerEntries.map(([playerId, playerData], playerIndex) => {
                            // Extract horseId from player data array (index 11) or from horseId property
                            const horseId = (playerData as any).horseId || 
                                           (Array.isArray(playerData) && playerData.length > 11 && playerData[11] !== null && playerData[11] !== undefined 
                                             ? String(playerData[11]) 
                                             : undefined);
                            return (
                              <PlayerItemMinimal
                                key={`${chukkerKey}-${teamId}-${playerId}`}
                                id={playerId}
                                managerMode={props.managerMode}
                                index={playerData[3]}
                                firstName={playerData[4]}
                                lastName={playerData[5]}
                                rating={props.useUSPARating ? playerData[6] : playerData[7]}
                                chukker={chukkerKey}
                                team={teamId}
                                isBench={false}
                                proColorMapping={props.proColorMapping}
                                betaMode={props.betaMode}
                                devPort={props.devPort}
                                clubId={props.clubId}
                                eventId={props.eventId}
                                eventType={props.eventType}
                                playerId={playerData[3]}
                                playerIndex={playerIndex}
                                horseId={horseId}
                                updateHorseAssignment={props.updateHorseAssignment}
                                chukkerList={props.chukkerList}
                                allHorseAssignments={props.allHorseAssignments}
                                allPlayerHorses={props.allPlayerHorses}
                                allClubHorses={props.allClubHorses}
                              />
                            );
                          })
                        )}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Box>
              );
            })}
          </React.Fragment>
        ))}

        {/* Bench Row */}
        {hasBench && (
          <>
            <Box
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 9,
                background: "linear-gradient(135deg, #800020 0%, #660019 100%)",
                p: 1,
                borderRight: "1px solid",
                borderTop: "2px solid",
                borderColor: "#800020",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gridRow: benchRowNumber,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: "white", fontSize: "0.75rem" }}>
                Bench
              </Typography>
            </Box>
            <Box
              sx={{
                gridColumn: `2 / ${chukkerKeys.length + 2}`,
                gridRow: benchRowNumber,
                backgroundColor: "rgba(128, 0, 32, 0.08)",
                p: 1.5,
                borderTop: "2px solid",
                borderColor: "#800020",
                minHeight: "120px",
                height: "120px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Droppable droppableId={`-1____A_-1`} type="TEAM">
                {(provided: any, snapshot: any) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      height: "100%",
                      minHeight: "40px",
                      backgroundColor: snapshot.isDraggingOver ? "rgba(128, 0, 32, 0.15)" : "transparent",
                      borderRadius: 1,
                      p: snapshot.isDraggingOver ? 0.5 : 0,
                      transition: "all 0.2s ease",
                      border: snapshot.isDraggingOver ? "2px dashed" : "none",
                      borderColor: snapshot.isDraggingOver ? "#800020" : "transparent",
                      alignContent: "flex-start",
                    }}
                  >
                    {Object.entries(benchData["A_-1"] || {}).map(([playerId, playerData], benchIndex) => {
                      // Extract horseId from player data array (index 11) or from horseId property
                      // Bench players typically don't have horses, but extract for consistency
                      const horseId = (playerData as any).horseId || 
                                     (Array.isArray(playerData) && playerData.length > 11 && playerData[11] !== null && playerData[11] !== undefined 
                                       ? String(playerData[11]) 
                                       : undefined);
                      return (
                        <PlayerItemMinimal
                          key={`bench-${playerId}`}
                          id={playerId}
                          managerMode={props.managerMode}
                          index={playerData[3]}
                          firstName={playerData[4]}
                          lastName={playerData[5]}
                          rating={props.useUSPARating ? playerData[6] : playerData[7]}
                          chukker="-1"
                          team="A_-1"
                          isBench={true}
                          proColorMapping={props.proColorMapping}
                          betaMode={props.betaMode}
                          devPort={props.devPort}
                          clubId={props.clubId}
                          eventId={props.eventId}
                          eventType={props.eventType}
                          playerId={playerData[3]}
                          playerIndex={benchIndex}
                          horseId={horseId}
                          updateHorseAssignment={props.updateHorseAssignment}
                          chukkerList={props.chukkerList}
                          allHorseAssignments={props.allHorseAssignments}
                          allPlayerHorses={props.allPlayerHorses}
                          allClubHorses={props.allClubHorses}
                        />
                      );
                    })}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Box>
          </>
        )}
      </Box>
      
      {/* Color Selection Menu */}
      {colorMenuAnchor && props.setChukkerDetails && (
        <Menu
          anchorEl={colorMenuAnchor.element}
          open={Boolean(colorMenuAnchor)}
          onClose={handleColorClose}
          onClick={(e) => e.stopPropagation()}
        >
          {Object.keys(PRO_COLORS_MAP).map((color) => (
            <MenuItem
              key={color}
              onClick={() => handleColorSelect(color)}
            >
              <img
                src={(PRO_COLORS_MAP as Record<string, string>)[color]}
                alt={`${color} jersey`}
                width={24}
                height={24}
                style={{ marginRight: 8 }}
              />
              {color}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );
}

export default ChukkerListDense;

